import { device } from '../../../constants/StyledComponentConstants';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Box } from 'foundations-components/transitional/components/Box';
import UsageTracker from '../../../utils/UsageTracker';
import ResponsiveCardGridContainer from '../../../components/ResponsiveCardGridContainer';
import { PageSectionHeading } from '../../../components/SectionHeading';
import { RouterLink } from '../../../components/AcademyLinks';
import styled, { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { NavMarker } from 'react-rhumb';
import { useMyLearningCardModels } from '../../myLearning/myLearningQueries';
import { MyLearningTabTypeToRequestMapping } from '../../myLearning/myLearningConsts';
import LearningContentCard from '../../cards/components/LearningContentCard';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { getMyLearningUrl } from '../../myLearning/myLearningRouting';
import HomePageSection from '../../../components/Layouts/HomePageSection';
import { useUserProgressState } from '../userProgressStore';
import { LearningContentCardsSkeleton } from '../../../components/Skeletons/LearningContentCardsSkeleton';
import { useDisplayedData } from '../../table/tableHooks';
import { useCallback } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CardBox = styled.div.withConfig({
  displayName: "InProgressSection__CardBox"
})(["width:100%;height:100%;&:not(:last-child){@media screen and ", "{margin-right:32px;flex-basis:auto;}}"], device.mobile);
const useInProgressSection = (numCards = 3) => {
  const {
    myLearningCardModels: inProgressItems,
    myLearningCardModelsLoading: inProgressItemsLoading,
    myLearningCardModelsError: inProgressItemsError,
    totalCount
  } = useMyLearningCardModels({
    contentTypes: MyLearningTabTypeToRequestMapping['all'],
    offset: {
      cursor: 0
    },
    pageLength: numCards,
    progressType: 'IN_PROGRESS',
    includePlaylistProgress: true
  });
  const {
    simulateNoProgress
  } = useUserProgressState();
  const {
    cardModels: displayedCardModels,
    totalCount: displayedTotalCount = 0
  } = useDisplayedData({
    cardModels: inProgressItems,
    totalCount
  }, {
    loading: inProgressItemsLoading,
    parameters: {},
    initialOnly: true
  }) || {};
  return {
    inProgressItems: simulateNoProgress ? [] : inProgressItems,
    displayedItems: simulateNoProgress ? [] : displayedCardModels,
    inProgressItemsLoading,
    inProgressItemsError,
    totalCount: displayedTotalCount
  };
};
const ViewAllLink = () => {
  const trackViewAllClick = () => UsageTracker.track('interaction', {
    action: 'in-progress-view-all-clicked'
  });
  return /*#__PURE__*/_jsx(RouterLink, {
    onClick: trackViewAllClick,
    to: `${getMyLearningUrl()}?progressStatus=IN_PROGRESS`,
    underline: true,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.inProgressSection.seeAll"
    })
  });
};
export const InProgressSection = ({
  cardSize = 'small',
  numCards = 3
}) => {
  const {
    inProgressItems,
    displayedItems,
    inProgressItemsError,
    inProgressItemsLoading,
    totalCount
  } = useInProgressSection(numCards);
  const {
    isMobile,
    isLaptop
  } = useMediaQuery();
  const isGhost = useCallback(item => !!inProgressItems && !inProgressItems.some(c => c.id === item.id && c.type === item.type), [inProgressItems]);
  if (inProgressItemsLoading && !(displayedItems !== null && displayedItems !== void 0 && displayedItems.length)) {
    return /*#__PURE__*/_jsxs(HomePageSection, {
      paddingBlock: "0",
      children: [/*#__PURE__*/_jsx(TFlex, {
        align: "center",
        marginBlockEnd: isMobile ? '200' : '400',
        children: /*#__PURE__*/_jsx(Box, {
          grow: 2,
          children: /*#__PURE__*/_jsx(PageSectionHeading, {
            marginBlockEnd: "0",
            subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.inProgressSection.sectionSubtitle"
            }),
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.inProgressSection.sectionTitle"
            })
          })
        })
      }), /*#__PURE__*/_jsx(ResponsiveCardGridContainer, {
        className: "noBorder noPadding",
        columnSize: 250,
        columns: isLaptop ? 1 : numCards,
        gutterSize: isMobile ? 8 : 20,
        nowrap: false,
        paddingBlockEnd: "0",
        scrollOnMobile: false,
        children: /*#__PURE__*/_jsx(LearningContentCardsSkeleton, {
          maxSkeletonListLength: numCards,
          skeletonCardSize: cardSize,
          use: "on-light"
        })
      })]
    });
  }
  if (inProgressItemsError || !(displayedItems !== null && displayedItems !== void 0 && displayedItems.length)) {
    return null;
  }
  const showViewMoreLink = totalCount > numCards;
  return /*#__PURE__*/_jsxs(HomePageSection, {
    paddingBlock: "0",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "HOMEPAGE_IN_PROGRESS_LOADED"
    }), /*#__PURE__*/_jsxs(TFlex, {
      align: "center",
      marginBlockEnd: isMobile ? '200' : '400',
      children: [/*#__PURE__*/_jsx(Box, {
        grow: 2,
        children: /*#__PURE__*/_jsx(PageSectionHeading, {
          marginBlockEnd: "0",
          subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.inProgressSection.sectionSubtitle"
          }),
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.inProgressSection.sectionTitle"
          })
        })
      }), showViewMoreLink && /*#__PURE__*/_jsx(Box, {
        alignSelf: "end",
        children: /*#__PURE__*/_jsx(ViewAllLink, {})
      })]
    }), /*#__PURE__*/_jsx(ResponsiveCardGridContainer, {
      className: "noBorder noPadding",
      columnSize: 250,
      columns: isLaptop ? 1 : numCards,
      gutterSize: isMobile ? 8 : 20,
      nowrap: false,
      paddingBlockEnd: "0",
      scrollOnMobile: false,
      style: {
        marginInline: 'unset',
        overflow: 'visible',
        paddingInline: 'unset'
      },
      children: displayedItems.map(item => /*#__PURE__*/_jsx(CardBox, {
        children: /*#__PURE__*/_jsx(LearningContentCard, {
          flush: true,
          isGhost: isGhost(item),
          item: item,
          overviewType: "my-learning",
          size: cardSize
        })
      }, `item_progress_${item.id}`))
    })]
  });
};
export default InProgressSection;